import { useState, useRef, Fragment, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from 'react-image-crop';
import { truncate } from 'lodash';

import 'react-image-crop/dist/ReactCrop.css';
import { IconButton, Stack, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/styles';

import CloudUploadRoundedIcon from '@material-ui/icons/CloudUploadRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';
// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

function ImageCrop({ src, disableSave, onCropComplete }) {
  const theme = useTheme();
  const imgRef = useRef(null);
  const [completedCrop, setCompletedCrop] = useState();
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState();
  const [aspect] = useState(1);
  const [imgSrc, setImgSrc] = useState(null);

  const filenameRef = useRef('');

  const handleImageUpload = (event) => {
    const imageFile = event.target.files[0];
    setSelectedImage(URL.createObjectURL(imageFile));
  };

  const handleResetImage = () => {
    setSelectedImage(null); // Reset the selected image to null
    setCrop({ aspect: 1 / 1 }); // Reset crop to default or your preferred values
  };

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current) {
        const croppedImageBlob = await getCroppedImageBlob();
        onCropComplete(croppedImageBlob);
      }
    },
    100,
    [completedCrop]
  );

  const getCroppedImageBlob = async () => {
    if (!selectedImage) return null;

    const image = new Image();
    image.src = selectedImage;

    return new Promise((resolve) => {
      image.onload = () => {
        const pixelCrop = convertToPixelCrop(crop, image.width, image.height);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );

        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg');
      };
    });
  };

  useEffect(() => {
    if (imgSrc === null) {
      setImgSrc(src);
    }
  }, [src]);

  useEffect(() => {
    if (selectedImage) {
      disableSave(false);
    } else {
      disableSave(true);
    }
  }, [selectedImage]);

  //on component unmount
  useEffect(() => {
    return () => {
      handleResetImage();
    };
  }, []);
  function onSelectFile(e) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Makes crop preview update between images.
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(e.target.files[0]);
      filenameRef.current.textContent = truncate(e.target.files[0].name, { length: 20 });
      handleImageUpload(e);
    }
  }

  function onImageLoad(e) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  return (
    <Fragment>
      <Stack
        p={2}
        direction="row"
        alignItems={'center'}
        gap={2}
        fullWidth
        justifyContent={'space-between'}
      >
        <Stack direction="row" alignItems={'center'} gap={2}>
          <input type="file" accept="image/*" onChange={onSelectFile} hidden id="image-upload" />
          <label
            for="image-upload"
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.incorta.contrastText,
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem'
            }}
          >
            <CloudUploadRoundedIcon />
            Upload
          </label>
          <Typography variant="caption" color="textSecondary" ref={filenameRef} overflow={'hidden'}>
            No image selected
          </Typography>
        </Stack>

        {imgSrc !== src && (
          <IconButton
            disabled={imgSrc === src}
            onClick={() => {
              handleResetImage();
              setImgSrc(src);
              filenameRef.current.textContent = 'No image selected';
            }}
            marginLeft="auto"
          >
            <CloseRoundedIcon />
          </IconButton>
        )}
      </Stack>
      {!!imgSrc &&
        (imgSrc === src ? (
          <img
            ref={imgRef}
            alt="Cropped Profile Picture"
            src={imgSrc}
            style={{ width: '100%', marginBottom: '0.5rem' }}
            onLoad={onImageLoad}
          />
        ) : (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            style={{ width: '100%' }}
            disabled={!imgSrc || imgSrc === src}
          >
            <img
              ref={imgRef}
              alt="Cropped Profile Picture"
              src={imgSrc}
              style={{ width: '100%' }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        ))}
    </Fragment>
  );
}

export default ImageCrop;
